import React, { useState, useEffect, useCallback } from "react"
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth"
import { TextField, Button, Container, Typography, Alert, Paper, Stack, Box } from "@mui/material"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { auth } from "@/firebase"
import icon from "@/images/pngs/icon.png"

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams()
  const oobCode: string | null = searchParams.get("oobCode")
  const [newPassword, setNewPassword] = useState<string>("")
  const [confirmPassword, setConfirmPassword] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  const removeErrors = useCallback(() => {
    setError("")
    setMessage("")
  }, [])

  useEffect(() => {
    if (!oobCode) {
      setError("Invalid or expired reset link.")
    }
  }, [oobCode])

  const handleResetPassword = async (): Promise<void> => {
    setMessage("")
    setError("")
    setLoading(true)

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.")
      setLoading(false)
      return
    }

    try {
      if (!oobCode) throw new Error("Invalid reset code.")
      await verifyPasswordResetCode(auth, oobCode)
      await confirmPasswordReset(auth, oobCode, newPassword)
      setMessage("Password has been reset successfully!")
      navigate("/")
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError("An unknown error occurred.")
      }
    }
    setLoading(false)
  }

  return (
    <Container
      maxWidth="sm"
      style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <Paper variant="outlined" style={{ padding: "20px", width: "100%" }}>
        <Stack gap={1}>
          <Box display="flex" justifyContent="center" mb={1}>
            <img src={icon} alt="Brotherhood" style={{ width: 72, height: 72 }} />
          </Box>
          <Typography variant="h5" gutterBottom>
            Reset Password
          </Typography>
          <TextField
            label="New Password"
            type="password"
            variant="outlined"
            size="small"
            fullWidth
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
          <TextField
            label="Confirm Password"
            type="password"
            variant="outlined"
            size="small"
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
          />
          {message && (
            <Alert severity="success" onClose={removeErrors}>
              {message}
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={removeErrors}>
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || !newPassword || !confirmPassword}
            onClick={handleResetPassword}>
            {loading ? "Updating..." : "Update Password"}
          </Button>
          <Button variant="text">
            <Link to="/">Home</Link>
          </Button>
        </Stack>
      </Paper>
    </Container>
  )
}

export default ResetPassword
